import { uploadIssueAttachmentService } from '@/service/fault-reporting.service'
import clsx from 'clsx'
import { useTranslations } from 'next-intl'
import PdMedia from '@/components/pd-media/pd-media'
import { useEffect, useRef, useState } from 'react'

export interface IssueAttachmentItem {
  id: string
  name: string
  ext: string
  progress: number
  status: 'uploading' | 'done' | 'error'
  url?: string
  error?: string
}

export interface IssueAttachmentUploadProps {
  locale: string
  disabled?: boolean
  onChange: (items: IssueAttachmentItem[]) => void
  onUploadingChange?: (isUploading: boolean) => void
}

function isImageFile(ext: string) {
  return ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(ext.toLowerCase())
}

export function IssueAttachmentUpload(props: IssueAttachmentUploadProps) {
  const { locale, disabled = false, onChange, onUploadingChange } = props
  const t = useTranslations()
  const fileInputRef = useRef<HTMLInputElement>(null)
  const [attachments, setAttachments] = useState<IssueAttachmentItem[]>([])
  const [uploadError, setUploadError] = useState<string | null>(null)

  const isUploading = attachments.some((item) => item.status === 'uploading')

  useEffect(() => {
    onChange(attachments)
    onUploadingChange?.(isUploading)
  }, [attachments, isUploading, onChange, onUploadingChange])

  function validateUploadFile(file: File) {
    const isImage = /^image\/(jpeg|jpg|png)$/i.test(file.type)
    const isVideo = /^video\/(mp4|mov|quicktime)$/i.test(file.type)
    if (!isImage && !isVideo)
      return t('FaultReportingForm.UploadTypeError')

    if (isImage && file.size > 5 * 1024 * 1024)
      return t('FaultReportingForm.UploadImageSizeError')

    if (isVideo && file.size > 100 * 1024 * 1024)
      return t('FaultReportingForm.UploadVideoSizeError')

    return ''
  }

  async function handleUploadChange(event: React.ChangeEvent<HTMLInputElement>) {
    const files = Array.from(event.target.files || [])
    if (!files.length)
      return

    event.target.value = ''

    const remain = 5 - attachments.length
    if (remain <= 0) {
      setUploadError(t('FaultReportingForm.UploadMaxCountError'))
      return
    }

    const picked = files.slice(0, remain)
    for (const file of picked) {
      const validateError = validateUploadFile(file)
      if (validateError) {
        setUploadError(validateError)
        continue
      }

      setUploadError(null)
      const id = `${file.name}-${Date.now()}-${Math.random().toString(16).slice(2)}`
      const ext = file.name.split('.').pop()?.toLowerCase() || ''
      setAttachments((prev) => [
        ...prev,
        {
          id,
          name: file.name,
          ext,
          progress: 0,
          status: 'uploading',
        },
      ])

      try {
        const uploaded = await uploadIssueAttachmentService(file, locale, {
          onProgress: (progress) => {
            setAttachments((prev) =>
              prev.map((item) => (item.id === id ? { ...item, progress } : item))
            )
          },
        })

        setAttachments((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: 'done',
                  url: uploaded.url,
                  ext: uploaded.ext || item.ext,
                  progress: 100,
                }
              : item
          )
        )
      } catch (error) {
        setAttachments((prev) =>
          prev.map((item) =>
            item.id === id
              ? {
                  ...item,
                  status: 'error',
                  error: (error as Error)?.message || t('FaultReportingForm.UploadFailed'),
                }
              : item
          )
        )
      }
    }
  }

  function handleRemoveAttachment(id: string) {
    setAttachments((prev) => prev.filter((item) => item.id !== id))
  }

  return (
    <div className={clsx('flex flex-col', 'gap-4 laptop:gap-2')}>
      <label className={clsx('text-16 laptop:text-14 font-semibold', 'text-black/90', 'leading-6 laptop:leading-[22px]')}>
        {t('FaultReportingForm.Attachments')}
      </label>
      <p className={clsx('text-14', 'text-black/60', 'leading-[22px]')}>
        {t('FaultReportingForm.UploadRules')}
      </p>

      <div className={clsx('flex', 'items-start', 'gap-1 laptop:gap-2', 'flex-wrap')}>
        {attachments.map((item) => (
          <div
            key={item.id}
            className={clsx(
              'relative',
              'w-[79px] h-[79px] laptop:w-[81px] laptop:h-[81px]',
              'bg-pdbg',
              'border border-[#BBC3CE]',
              'rounded-[2px]',
              'overflow-hidden'
            )}
          >
            {isImageFile(item.ext) && item.url
              ? (
                <PdMedia src={item.url} alt={item.name} mask={false} object="cover" />
              )
              : (
                <div className={clsx('w-full h-full', 'flex items-center justify-center', 'px-1')}>
                  <span className={clsx('text-12', 'text-black/60', 'truncate')}>
                    {item.ext.toUpperCase() || 'FILE'}
                  </span>
                </div>
              )}

            {item.status === 'uploading' && (
              <div
                className={clsx(
                  'absolute bottom-0 left-0 right-0',
                  'bg-black/50 text-white text-10',
                  'px-1 py-0.5'
                )}
              >
                {`${Math.floor(item.progress)}%`}
              </div>
            )}

            {item.status === 'error' && (
              <div
                className={clsx(
                  'absolute inset-0',
                  'bg-black/45',
                  'flex items-center justify-center',
                  'text-[#F52242] text-12'
                )}
              >
                !
              </div>
            )}

            <button
              type="button"
              className={clsx(
                'absolute right-0 top-0 -translate-y-1/2 translate-x-1/2',
                'laptop:right-1 laptop:top-1 laptop:translate-x-0 laptop:translate-y-0',
                'w-4 h-4 rounded-full',
                'bg-[#F52242] laptop:bg-black/55 text-white text-10',
                'flex items-center justify-center'
              )}
              onClick={() => handleRemoveAttachment(item.id)}
              aria-label={t('FaultReportingForm.Remove')}
            >
              x
            </button>
          </div>
        ))}

        {attachments.length < 5 && (
          <button
            type="button"
            className={clsx(
              'w-[79px] h-[79px] laptop:w-[81px] laptop:h-[81px]',
              'bg-pdbg',
              'border border-[#BBC3CE]',
              'rounded-[2px]',
              'flex items-center justify-center',
              'hover:opacity-90 transition-opacity',
              'disabled:opacity-60'
            )}
            onClick={() => fileInputRef.current?.click()}
            disabled={disabled}
            aria-label={t('FaultReportingForm.UploadFile')}
          >
            <span className={clsx('relative', 'block', 'w-6 h-6')}>
              <span
                className={clsx(
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                  'w-6 h-px bg-[#BBC3CE]'
                )}
              />
              <span
                className={clsx(
                  'absolute left-1/2 top-1/2 -translate-x-1/2 -translate-y-1/2',
                  'w-px h-6 bg-[#BBC3CE]'
                )}
              />
            </span>
          </button>
        )}

        <input
          ref={fileInputRef}
          type="file"
          multiple
          className={clsx('hidden')}
          accept=".jpg,.jpeg,.png,.mp4,.mov,video/quicktime,image/jpeg,image/png,video/mp4"
          onChange={handleUploadChange}
        />
      </div>

      {uploadError && <p className={clsx('text-14', 'text-[#F52242]')}>{uploadError}</p>}
    </div>
  )
}
